import * as React from 'react';
import {
  Box,
  Button,
  FormHelperText,
  Grid,
  MenuItem,
  Modal,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { createColumn } from '@/features/crm/api/createColumn';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { enqueueSnackbar } from 'notistack';
import { ColumnModel } from '@/features/crm/types';
import useLoadingActions from '@/hooks/useLoadingActions.tsx';
import { TypesCRM } from '@/types/crm.ts';
import { useListColumns } from '@/features/crm/api/listColumns.ts';

type CreateColumnCrmProps = {
  handleClose: () => void;
  type: TypesCRM
};

const CreateColumnCrm: React.FC<CreateColumnCrmProps> = ({ handleClose, type }) => {
  const { setIsLoading } = useLoadingActions();
  const { refetch } = useListColumns(type);

  const formik = useFormik<ColumnModel>({
    initialValues: {
      nome: '',
      icon: '',
      ordem: '',
      tipo: type,
    } as unknown as ColumnModel,
    validationSchema: Yup.object({
      nome: Yup.string().required('Campo obrigatório'),
      icon: Yup.string().required('Campo obrigatório'),
      ordem: Yup.number().required('Campo obrigatório'),
      tipo: Yup.number().required('Campo obrigatório'),
    }),
    onSubmit: async (values) => {
      try {
        setIsLoading(true, 'Adicionando coluna...');

        await createColumn(values);

        enqueueSnackbar('Coluna adicionada com sucesso!', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });

        refetch().then()

        handleClose();
      } catch (e) {
        enqueueSnackbar((e as Error).message || 'Falha ao adicionar coluna!', {
          variant: 'error',
          anchorOrigin: { vertical: 'top', horizontal: 'center' },
        });
      } finally {
        setIsLoading(false, '');
      }
    },
  });

  const style = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    maxWidth: '517px',
    width: '100%',
    bgcolor: '#fff',
    borderRadius: '20px',
  };

  return (
    <div>
      <Modal
        open={true}
        onClose={handleClose}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}
             component={'form'}
             noValidate
             onSubmit={formik.handleSubmit}
        >
          <Box style={{ background: '#93DC3F', borderRadius: '20px 20px 0 0', boxShadow: '0px 4px 4px 0px #00000040' }}>
            <Typography color={'#231F20'} variant="h1" p={'10px 29px'}>
              Dados da coluna
            </Typography>
          </Box>
          <Box sx={{ p: '20px 30px 50px 30px' }}>
            <Grid container spacing={2} justifyContent="center" mb={2}>
              <Grid item xs={12} sm={6}>
                <Typography>Nome*</Typography>
                <TextField
                  placeholder="Backlog"
                  required
                  id="nome"
                  name="nome"
                  fullWidth
                  value={formik.values.nome}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.nome && !!formik.errors.nome}
                  helperText={
                    formik.touched.nome &&
                    formik.errors.nome &&
                    formik.errors.nome
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Icon*</Typography>
                <TextField
                  placeholder="iamter"
                  required
                  id="icon"
                  name="icon"
                  fullWidth
                  value={formik.values.icon}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.icon && !!formik.errors.icon}
                  helperText={
                    formik.touched.icon &&
                    formik.errors.icon &&
                    formik.errors.icon
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Ordem*</Typography>
                <TextField
                  placeholder="1"
                  type="number"
                  required
                  id="ordem"
                  name="ordem"
                  fullWidth
                  value={formik.values.ordem}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.ordem && !!formik.errors.ordem}
                  helperText={
                    formik.touched.ordem &&
                    formik.errors.ordem &&
                    formik.errors.ordem
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography>Tipo</Typography>
                <Select
                  fullWidth
                  name="tipo"
                  id="tipo"
                  sx={{ border: '1px solid #000', maxWidth: '350px' }}
                  value={formik.values.tipo}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.tipo && !!formik.errors.tipo}
                  inputProps={{ readOnly: true }}
                >
                  <MenuItem value={TypesCRM.MATRICULA}>CRM Matrícula</MenuItem>
                  <MenuItem value={TypesCRM.REMATRICULA}>CRM Rematrícula</MenuItem>
                  <MenuItem value={TypesCRM.INADIMPLENCIA}>CRM Inadimplência</MenuItem>
                </Select>
                {
                  formik.touched.tipo && formik.errors.tipo &&
                  <FormHelperText>{formik.errors.tipo}</FormHelperText>
                }
              </Grid>
            </Grid>

            <Stack direction="row" spacing={2} justifyContent="center">
              <Button
                sx={{ width: '164px', borderRadius: '24px', fontSize: '14px', mt: '40px', bgcolor: '#D63429' }}
                variant="contained"
                onClick={handleClose}
              >
                Descartar
              </Button>
              <Button
                sx={{ width: '164px', borderRadius: '24px', fontSize: '14px', mt: '40px', bgcolor: '#15AB51' }}
                variant="contained"
                type={'submit'}
              >
                Salvar
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
    </div>
  );
};

export default CreateColumnCrm;
